import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelect from "src/components/LanguageSelect/LanguageSelect";
import { APP_INFO } from "src/config/appInfo";
import "./login.scss";
import "./loginForm.scss";
import { PasswordInput } from "src/components/PasswordInput/PasswordInput";

export default function ResetPassword() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") ?? "";

    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const handleSubmit = async () => {
        setError("");
        if (newPassword.length < 8) {
            setError(t("login.resetPasswordTooShort"));
            return;
        }
        if (newPassword !== confirm) {
            setError(t("login.resetPasswordMismatch"));
            return;
        }
        setLoading(true);
        try {
            const res = await fetch("/api/reset-password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, newPassword }),
            });
            if (res.ok) {
                setSuccess(true);
                setTimeout(() => navigate("/login"), 2500);
            } else {
                const data = await res.json().catch(() => ({}));
                if (data.message === "INVALID_OR_EXPIRED_TOKEN") {
                    setError(t("login.resetPasswordInvalid"));
                } else if (data.message === "PASSWORD_TOO_SHORT") {
                    setError(t("login.resetPasswordTooShort"));
                } else {
                    setError(t("login.invalidCredentials"));
                }
            }
        } catch {
            setError(t("login.invalidCredentials"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-card-wrapper">
                <div className="login-card">
                    <LanguageSelect />
                    <div className="login-header">
                        <div className="login-logo">
                            <img src="/medinvoice-logo.png" alt={APP_INFO.appName} className="login-brand-logo" />
                        </div>
                        <div className="login-title">{t("login.resetPasswordTitle")}</div>
                        <div className="login-subtitle">{t("login.resetPasswordSubtitle")}</div>
                    </div>

                    {success ? (
                        <div className="forgot-password-sent">
                            {t("login.resetPasswordSuccess")}
                        </div>
                    ) : (
                        <form onSubmit={(e) => e.preventDefault()}>
                            <div className="form-group">
                                <label htmlFor="rp-new">{t("login.resetPasswordNew")}</label>
                                <PasswordInput
                                    id="rp-new"
                                    placeholder={t("login.resetPasswordNew")}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    onKeyUp={(e) => e.key === "Enter" && handleSubmit()}
                                    autoComplete="new-password"
                                    autoFocus
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="rp-confirm">{t("login.resetPasswordConfirm")}</label>
                                <PasswordInput
                                    id="rp-confirm"
                                    placeholder={t("login.resetPasswordConfirm")}
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    onKeyUp={(e) => e.key === "Enter" && handleSubmit()}
                                    autoComplete="new-password"
                                />
                            </div>
                            <div className="login-error">{error}</div>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={handleSubmit}
                                disabled={loading || !newPassword || !confirm}
                            >
                                {loading ? <span className="btn-spinner" /> : t("login.resetPasswordSubmit")}
                            </button>
                        </form>
                    )}

                    <div className="login-register">
                        <Link to="/login" className="login-contact-link">
                            {t("login.forgotPasswordBack")}
                        </Link>
                    </div>
                </div>
            </div>

            <div className="login-art">
                <div className="login-art__stage">
                    <div className="login-art__blob" />
                    <div className="login-art__panel login-art__panel--visible"
                         style={{ backgroundImage: "url(/poster_1.webp)" }} />
                </div>
            </div>
        </div>
    );
}
